import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { ConstantService } from '../../services/common/constant.service';
import { AuthMethodType, AuthMethodViewModel, AuthenticationMethodConfigurationViewModel } from '../../services/identity';
import { EnrollmentInputModel } from '../enrollment/models/enrollmentinput.model';
import { BaseComponent } from './methods/base/base.component';

import { LaddaModule } from 'angular2-ladda';

@Component({
    selector: 'app-enrollment',
    templateUrl: './enrollment.component.html',
    styleUrl: './enrollment.component.scss',
    imports: [BaseComponent, LaddaModule, TranslatePipe]
})
export class EnrollmentComponent implements OnInit, OnDestroy {
  private constantService = inject(ConstantService);
  private toastrService = inject(ToastrService);
  private translateService = inject(TranslateService);
  private changeDetectorRef = inject(ChangeDetectorRef);


  @Input() options!: EnrollmentInputModel;
  @Input() hideSaveButton = false;
  @Input() useExternalToast = false;
  @Output() callback: EventEmitter<any> = new EventEmitter();
  @Output() backPressedEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() saveStateChanged = new EventEmitter<{ canSave: boolean; hasChanges: boolean }>();
  @Output() saveFailed = new EventEmitter<string | null>();

  private subscriptions: Subscription = new Subscription;

  private authMethodConfig: AuthenticationMethodConfiguration;

  private form: FormGroup;

  private methods: AuthMethod[] = [];
  private originalMethods: AuthMethodViewModel[];
  private usersMethods: AuthMethodViewModelWithIndex[];
  private firstFactorMethods: AuthMethodViewModelWithIndex[];
  private secondFactorMethods: AuthMethodViewModelWithIndex[];

  private formStatuses: FormStatus[] = [];
  private methodRemoved: boolean = false;
  private saveButtonEnabled: boolean = false;

  private canAddFirstFactor: boolean;
  private canAddSecondFactor: boolean;

  private isSelf: boolean;

  private isSaving: boolean;

  @ViewChildren('firstFactorComponent') firstFactorComponents: QueryList<BaseComponent>;
  @ViewChildren('secondFactorComponent') secondFactorComponents: QueryList<BaseComponent>;

  ngOnInit(): void {
    this.methods = [];
    this.methodRemoved = false;
    this.formStatuses = [];
    this.saveButtonEnabled = false;
    this.saveStateChanged.emit({ canSave: false, hasChanges: false });
    this.isSelf = this.options.user.id == 0 || this.options.currentUser.id == this.options.user.id;


    let getMethodsEndpoint: Observable<any>;
    if (this.isSelf) {
      getMethodsEndpoint = this.options.functions.getAuthMethods();
    } else {
      getMethodsEndpoint = this.options.functions.getAuthMethodsForUser(this.options.org.mnemonic, this.options.user.username);
    }
    this.subscriptions.add(
      forkJoin(
        [
          this.options.functions.getConfig(this.options.org.id, this.options.user.id),
          getMethodsEndpoint,
          this.options.functions.getOrgSettings(this.options.org.id, 'MFA')
        ])
        .subscribe({
          next: ([config, methods, mfaOptions]) => {
            this.authMethodConfig = config as AuthenticationMethodConfiguration;
            //this.options.state.disablePrimaryFactor = config.disablePrimaryFactory;
            this.options.state.allowMultiplePrimaryFactor = this.canAddFirstFactor = config.allowMultipleFirstFactors && (!this.options.state.disablePrimaryFactor || !this.isSelf);
            this.options.state.allowMultipleSecondaryFactor = this.canAddSecondFactor = config.allowMultipleSecondFactors;
            this.options.state.enforceSecondaryFactor = config.enforceSecondFactor;

            this.createMethods();

            this.originalMethods = methods;
            this.usersMethods = methods.map((x, index) => ({ ...x, index: index }));
            this.disableAssignedMethods();
            this.updateFirstAndSecondFactorMethods();
            this.setupFormStatuses();
            

            this.handleMfaOptions(config, mfaOptions);
            this.changeDetectorRef.markForCheck();
          },
          error: error => { console.error('Failed to load enrollment options', error); }
        }));
  }

  disableAssignedMethods() {
    this.usersMethods.forEach(x => {
      let method = this.methods.find(m => m.id == x.type);
      if (method) {
        method.disabled = true;
      }
    })
  }

  updateFirstAndSecondFactorMethods() {
    this.firstFactorMethods = this.usersMethods.filter(x => x.sequence == 1);
    this.secondFactorMethods = this.usersMethods.filter(x => x.sequence == 2);
    this.canAddSecondFactor = this.secondFactorMethods.length == 0 || this.options.state.allowMultipleSecondaryFactor;
  }

  updateMethodIndexes() {
    this.usersMethods.forEach((x, i) => {
      x.index = i;
    });
  }


  createMethods() {    
    let passcodeEmailAllowed = false;
    let passcodeSmsAllowed = false;
    let staticCodeAllowed = false;
    let softTokenAllowed = false;
    let hardwareTokenAllowed = false;
    let yubikeyAllowed = false;
    let fidoTokenAllowed = false;
    if (this.checkMethodAllowed(this.authMethodConfig.passcode, ['PasscodeSms', 'PasscodeEmail'])) {      
      if (this.authMethodConfig.passcodeViaEmail) {
        passcodeEmailAllowed = true;
      }
      if (this.authMethodConfig.passcodeViaSms) {
        passcodeSmsAllowed = true;
      }
    }

    if (this.checkMethodAllowed(this.authMethodConfig.staticCode, ['StaticCode'])) {
      staticCodeAllowed = true;
    }
    if (this.checkMethodAllowed(this.authMethodConfig.softToken, ['SoftToken'])) {
      softTokenAllowed = true;
    }
    if (this.checkMethodAllowed(this.authMethodConfig.hardwareToken, ['HardwareToken'])) {
      hardwareTokenAllowed = true;
    }
    if (this.checkMethodAllowed(this.authMethodConfig.yubikey, ['Yubikey'])) {
      yubikeyAllowed = true;
    }
    if (this.checkMethodAllowed(this.authMethodConfig.fidoToken, ['Fido'])) {
      fidoTokenAllowed = true;
    }
    this.methods.push({ id: 'Password', name: 'Password', disabled: false, allowedForUser: true });
    this.methods.push({ id: 'PasscodeSms', name: 'Passcode Via SMS', disabled: !passcodeSmsAllowed, allowedForUser: passcodeSmsAllowed });
    this.methods.push({ id: 'PasscodeEmail', name: 'Passcode Via Email', disabled: !passcodeEmailAllowed, allowedForUser: passcodeEmailAllowed });
    this.methods.push({ id: 'StaticCode', name: 'Static Code', disabled: !staticCodeAllowed, allowedForUser: staticCodeAllowed });
    this.methods.push({ id: 'SoftToken', name: 'Soft Token / Push', disabled: !softTokenAllowed, allowedForUser: softTokenAllowed });
    this.methods.push({ id: 'HardwareToken', name: 'Hardware Token', disabled: !hardwareTokenAllowed, allowedForUser: hardwareTokenAllowed });
    this.methods.push({ id: 'Yubikey', name: 'Yubikey', disabled: !yubikeyAllowed, allowedForUser: yubikeyAllowed });
    this.methods.push({ id: 'Fido', name: 'Fido Token', disabled: !fidoTokenAllowed, allowedForUser: fidoTokenAllowed });
  }

  checkMethodAllowed(method: AuthenticationMethodFlags, types: AuthMethodType[]) {
    if ((method & AuthenticationMethodFlags.AllowedAsTemp) === AuthenticationMethodFlags.AllowedAsTemp) {
      this.options.state.allowedTempTypes.push(...types);
    }
    if ((method & AuthenticationMethodFlags.Enabled) === AuthenticationMethodFlags.Enabled) {
      if (this.isSelf && this.options.currentUser.type != 'superadmin' && this.options.currentUser.type != 'mspadmin' && this.options.currentUser.type != 'adminuser') {
        if ((method & AuthenticationMethodFlags.UserSelectable) === AuthenticationMethodFlags.UserSelectable) {
          return true;
        }
        return false;
      }
      return true;
    }
    return false;
  }

  handleMfaOptions(authConfig: AuthenticationMethodConfigurationViewModel, mfaOptions: any) {
    let userCodeFormat = JSON.parse(mfaOptions.find((x: any) => x.key == 'UserCodeFormat')?.value).MinimumLength;
    let fidoAllowMultiple = JSON.parse(mfaOptions.find((x: any) => x.key == 'FidoAllowMultiple')?.value ?? 'false');
    let fidoAllowUsernameless = JSON.parse(mfaOptions.find((x: any) => x.key == 'FidoAllowResidentKey')?.value ?? 'false');
    let softTokenType = mfaOptions.find((x: any) => x.key == 'SoftTokenAuthenticatorType')?.value ?? 'securenvoy';
    let pushAllowed = !JSON.parse(mfaOptions.find((x: any) => x.key == 'DisableSoftTokenPush')?.value ?? 'false');

    this.options.state.staticCode = {
      minLength: userCodeFormat
    }

    this.options.state.softToken = {
      pushEnabled: true,
      pushEnforced: authConfig.enforcePush,
      pushAllowed: pushAllowed,
      authenticatorType: softTokenType,
    }

    this.options.state.fido = {
      allowMultipleTokens: fidoAllowMultiple,
      allowResidentKey: fidoAllowUsernameless
    }
  }

  typeChange(type: MethodTypeChange) {
    let previousMethod = this.methods.find(x => x.id == type.previousType);
    if (previousMethod && previousMethod.allowedForUser) {
      previousMethod.disabled = false;
    }
    let newMethod = this.methods.find(x => x.id == type.newType);
    if (newMethod) {
      newMethod.disabled = true;
    }
    this.methods = [...this.methods];
  }

  addMethod(sequence: number): void {
    const existing = this.usersMethods.filter(x => x.sequence == sequence).length;
    let isDefault = false;
    if (existing == 0) {
      isDefault = true;
    }
    this.usersMethods.push({ sequence: sequence, index: this.usersMethods.length, isDefault: isDefault });
    this.formStatuses.push({ sequence: sequence, index: this.usersMethods.length - 1, isValid: true, touched: false })
    this.updateFirstAndSecondFactorMethods();
    this.checkSaveButtonEnabled();

  }

  removeMethod(data: RemoveMethodData): void {
    let formStatusIndex: number;
    this.usersMethods.splice(data.index, 1);
    formStatusIndex = this.formStatuses.findIndex(x => x.sequence == data.sequence && x.index == data.index);
    this.formStatuses.splice(formStatusIndex, 1);
    this.methodRemoved = true;
    this.updateFirstAndSecondFactorMethods();
    this.updateMethodIndexes();
    this.checkSaveButtonEnabled();
  }

  private setupFormStatuses(): void {
    this.usersMethods.forEach((x, i) => {
      this.formStatuses.push({ sequence: x.sequence, index: i, isValid: true, touched: false});
    })
  }

  private formStatusChange(data: FormStatusData): void {
    const status = this.formStatuses.find(x => x.sequence == data.sequence && x.index == data.index);
    if (status) {
      status.isValid = data.status == 'VALID';
      status.touched = data.touched ?? false;
    }
    this.checkSaveButtonEnabled();
  }

  private checkSaveButtonEnabled(): void {
    const allFormsValid = this.formStatuses.every(x => x.isValid);
    const anyTouched = this.formStatuses.some(x => x.touched);
    this.saveButtonEnabled = allFormsValid && (anyTouched || this.methodRemoved);
    this.saveStateChanged.emit({
      canSave: this.saveButtonEnabled && !this.isSaving,
      hasChanges: anyTouched || this.methodRemoved || this.usersMethods.length !== this.originalMethods.length,
    });
  }

  private defaultChange(data: DefaultChangeData): void {
    if (data.sequence == 1) {
      this.firstFactorComponents.forEach(x => {
        if (x.index != data.index) {
          x.removeDefault();
        }
      })
    } else if (data.sequence == 2) {
      this.secondFactorComponents.forEach(x => {
        if (x.index != data.index) {
          x.removeDefault();
        }
      })
    }
    const status = this.formStatuses.find(x => x.sequence == data.sequence && x.index == data.index);
    if (status) {
      status.touched = true;
    }
    this.checkSaveButtonEnabled();
  }

  private resetAllComponents(): void {
    this.firstFactorComponents.forEach(x => {
      x.reset();
    });
    this.secondFactorComponents.forEach(x => {
      x.reset();
    });
  }

  private back(): void {
    this.backPressedEvent.emit();
  }

  save(): void {
    if (!this.saveButtonEnabled || this.isSaving) return;
    this.isSaving = true;
    this.checkSaveButtonEnabled();
    let updatedMethods: AuthMethodViewModel[] = []
    if (this.options.state.hidePrimaryFactor) {
      updatedMethods.push(...this.originalMethods.filter(x => x.sequence == 1));
    } else {
      this.firstFactorComponents.forEach(x => {
        updatedMethods.push(x.getMethod());
      });
    }

    if (this.options.state.hideSecondaryFactor) {
      updatedMethods.push(...this.originalMethods.filter(x => x.sequence == 2));
    } else {
      this.secondFactorComponents.forEach(x => {
        updatedMethods.push(x.getMethod());
      });
    }

    updatedMethods.forEach(x => {
      x['_type'] = x.type;
    })

    let endpoint: Observable<any>;
    if (this.isSelf) {
      endpoint = this.options.functions.saveAuthMethods(updatedMethods);
    } else {
      endpoint = this.options.functions.saveAuthMethodsForUser(this.options.org.mnemonic, this.options.user.username, this.options.user.id, updatedMethods)
    }
    this.subscriptions.add(
      endpoint
        .subscribe({
          next: data => {
            if (!this.useExternalToast) {
              this.toastrService.success(this.translateService.instant('Action.Success'));
            }
            this.isSaving = false;
            this.changeDetectorRef.markForCheck();
            if (this.callback.observed) {
              this.callback.emit();
            } else {
              this.reload();
            }
            
          },
          error: error => {
            const message = Array.isArray(error?.error) ? error.error[0]?.message : null;
            if (this.useExternalToast) {
              this.saveFailed.emit(message);
            } else if (message) {
              this.toastrService.clear();
              this.toastrService.error(message, this.translateService.instant('Action.Error'), this.constantService.ToastError);
            }
            this.isSaving = false;
            this.checkSaveButtonEnabled();
            this.changeDetectorRef.markForCheck();
          }
        })
    )

  }

  reload(): void {
    this.resetAllComponents();
    this.ngOnInit();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}

export interface AuthMethod {
  id: string,
  name: string
  disabled: boolean,
  allowedForUser: boolean
}

interface AuthenticationMethodConfiguration {
  id: number;
  organizationId: number;
  allowSecondFactor: boolean;
  enforceSecondFactor: boolean;
  allowMultipleFirstFactors: boolean;
  allowMultipleSecondFactors: boolean;
  passcode: AuthenticationMethodFlags;
  passcodeViaSms: boolean;
  passcodeViaEmail: boolean;
  softToken: AuthenticationMethodFlags;
  enforcePush: boolean;
  staticCode: AuthenticationMethodFlags;
  hardwareToken: AuthenticationMethodFlags;
  yubikey: AuthenticationMethodFlags;
  fidoToken: AuthenticationMethodFlags;
}

enum AuthenticationMethodFlags {
  None = 0,
  Enabled = 1 << 0,
  UserSelectable = 1 << 1,
  AllowedAsTemp = 1 << 2
};

interface FormStatus {
  sequence: number,
  index: number,
  isValid: boolean,
  touched: boolean
}

interface AuthMethodViewModelWithIndex extends AuthMethodViewModel {
  index: number;
}

export interface MethodTypeChange {
  previousType: string,
  newType: string
}

export interface RemoveMethodData {
  sequence: number,
  index: number
}

export interface FormStatusData {
  sequence: number,
  index: number,
  status: string,
  touched?: boolean
}

export interface DefaultChangeData {
  sequence: number,
  index: number
}
